// RecordingBoothScreen — lifted verbatim from the MagicPatterns mockup
// (handoff 2026-06-25), wired to live arc state + real silence-detect from
// the v0 RecordingRoom + upload via postTake.
//
// Visual structure is unchanged from the mockup. Only data sources changed:
//   - SCENE is built from arc.parsedScript + arc.selectedCharacter
//   - Auto-advance is real voice-activity detection, not a 3.5s interval
//   - Tap-to-record fires a real MediaRecorder; stop uploads to backend
//     and navigates to /run-lines
import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MicIcon, SquareIcon, XIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useArc } from '../state/arc';
import { postTake, TakeManifestEntry } from '../api/client';

type Line = {
  speaker: 'YOU' | 'OTHER';
  character: string;
  text: string;
  lineId: number;
};

// Voice activity / silence-advance thresholds — lifted from the v0
// RecordingRoom. Tuned for typical phone-mic gain in a quiet room.
const SILENCE_THRESHOLD = 0.018;
const SILENCE_HOLD_MS = 700;
const VOICE_ARMING_MS = 220;

export function RecordingBoothScreen() {
  const navigate = useNavigate();
  const { state, update } = useArc();
  const script = state.parsedScript;
  const projectId = state.projectId;
  const character = state.selectedCharacter;

  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [time, setTime] = useState(0);
  const [idx, setIdx] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Build the scene from real script data — all spoken lines in order,
  // tagged by speaker relative to the actor's character.
  const SCENE: Line[] = React.useMemo(() => {
    if (!script) return [];
    return script.lines
      .filter((l) => l.character && l.text?.trim())
      .map((l) => ({
        speaker: (l.character || '').toUpperCase() === (character || '').toUpperCase() ? 'YOU' : 'OTHER',
        character: l.character || '',
        text: l.text,
        lineId: l.id,
      }));
  }, [script, character]);

  // --- Recording plumbing ------------------------------------------------
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const startWallMsRef = useRef<number>(0);
  const cueStartMsRef = useRef<number>(0);
  const idxRef = useRef(idx);
  idxRef.current = idx;
  const recordingRef = useRef(recording);
  recordingRef.current = recording;
  const manifestRef = useRef<TakeManifestEntry[]>([]);
  const voiceTimerRef = useRef<{ voiceStartMs: number | null; silenceStartMs: number | null }>({
    voiceStartMs: null,
    silenceStartMs: null,
  });

  const nowMs = () => (startWallMsRef.current ? performance.now() - startWallMsRef.current : 0);

  const closeCurrentSegment = () => {
    const i = idxRef.current;
    if (i >= SCENE.length) return;
    const lineId = SCENE[i].lineId;
    const start = cueStartMsRef.current;
    const end = nowMs();
    manifestRef.current.push({ line_id: lineId, start_ms: Math.round(start), end_ms: Math.round(end) });
  };

  const advanceCue = () => {
    closeCurrentSegment();
    cueStartMsRef.current = nowMs();
    setIdx((i) => {
      if (i + 1 >= SCENE.length) {
        // End of script — stop the take automatically
        if (recordingRef.current) stopRecording();
        return i;
      }
      return i + 1;
    });
  };

  // VAD loop — same primitive as v0 RecordingRoom
  function startAnalyserLoop() {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const buf = new Uint8Array(analyser.fftSize);
    const tick = () => {
      if (!analyserRef.current) return;
      analyser.getByteTimeDomainData(buf);
      let amp = 0;
      const bands = 40;
      const step = Math.floor(buf.length / bands);
      for (let i = 0; i < bands; i++) {
        let mx = 0;
        for (let j = 0; j < step; j++) {
          const v = Math.abs(buf[i * step + j] - 128) / 128;
          if (v > mx) mx = v;
        }
        amp += mx;
      }
      amp /= bands;

      const now = performance.now();
      const vt = voiceTimerRef.current;
      if (amp > SILENCE_THRESHOLD) {
        if (vt.voiceStartMs === null) vt.voiceStartMs = now;
        vt.silenceStartMs = null;
      } else {
        if (vt.silenceStartMs === null) vt.silenceStartMs = now;
        if (
          recordingRef.current &&
          vt.voiceStartMs !== null &&
          now - vt.voiceStartMs >= VOICE_ARMING_MS &&
          now - vt.silenceStartMs >= SILENCE_HOLD_MS
        ) {
          vt.voiceStartMs = null;
          vt.silenceStartMs = null;
          advanceCue();
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }

  async function startRecording() {
    setError(null);
    setIdx(0);
    setTime(0);
    manifestRef.current = [];
    cueStartMsRef.current = 0;
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const ctx = new AudioContext();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.6;
      source.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;

      const rec = new MediaRecorder(stream);
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        await uploadTake(blob);
      };
      mediaRecorderRef.current = rec;
      startWallMsRef.current = performance.now();
      rec.start(250);
      setRecording(true);
      startAnalyserLoop();
    } catch (e: any) {
      setError(e?.message ?? "couldn't access mic");
    }
  }

  function stopRecording() {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    closeCurrentSegment();
    try { mediaRecorderRef.current?.stop(); } catch { /* no-op */ }
    setRecording(false);
    // Release mic
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    try { audioCtxRef.current?.close(); } catch { /* no-op */ }
    audioCtxRef.current = null;
    analyserRef.current = null;
  }

  async function uploadTake(blob: Blob) {
    if (!projectId) return;
    setUploading(true);
    try {
      const durationMs = Math.round(nowMs());
      const take = await postTake(projectId, blob, manifestRef.current, durationMs);
      update({ takeId: take.take_id });
      navigate('/run-lines', { replace: true });
    } catch (e: any) {
      setError('Upload failed: ' + (e?.message ?? 'unknown'));
    } finally {
      setUploading(false);
    }
  }

  // Recording timer
  useEffect(() => {
    if (!recording) return;
    const id = window.setInterval(() => setTime((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [recording]);

  // Mic cleanup if component unmounts mid-record
  useEffect(() => {
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      try { mediaRecorderRef.current?.stop(); } catch { /* no-op */ }
      streamRef.current?.getTracks().forEach((t) => t.stop());
      try { audioCtxRef.current?.close(); } catch { /* no-op */ }
    };
  }, []);

  const toggleRecording = () => {
    if (uploading) return;
    if (!recording) {
      if (idx >= SCENE.length - 1) setIdx(0);
      setTime(0);
      startRecording();
    } else {
      stopRecording();
    }
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // --- Guard: no script / no character → bounce to import or library
  if (!script || !projectId || !character) {
    return (
      <div className="h-full w-full bg-cream-light flex items-center justify-center text-gray-600 p-8 text-center">
        <p className="font-serif italic">
          No script loaded yet. Tap your invite link to bring one in.
        </p>
      </div>
    );
  }

  const sceneLabel = script.title.toUpperCase();

  return (
    <div className="h-full w-full bg-theater-dark relative overflow-hidden flex flex-col text-white">
      {/* Subtle Aurora Edge Lighting — pulses gently when recording */}
      <motion.div
        className="absolute top-0 left-0 right-0 h-[200px] bg-gradient-to-b from-rose-900/10 to-transparent pointer-events-none z-0"
        animate={{ opacity: recording ? [0.6, 1, 0.6] : 0.6 }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute bottom-0 left-0 right-0 h-[300px] bg-gradient-to-t from-rose-900/10 to-transparent pointer-events-none z-0"
        animate={{ opacity: recording ? [0.5, 0.9, 0.5] : 0.5 }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
      />

      {/* Recessed Manuscript Anchor — CSS leather fallback (per handoff:
          preserve both image + CSS-fallback paths; this script has no
          generated cover yet). */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0 opacity-[0.06] mix-blend-screen">
        <div className="leather-cover leather-navy" style={{ width: 320, height: 480 }} />
      </div>

      {/* Header — fades out during recording to remove distractions */}
      <motion.div
        animate={{ opacity: recording ? 0 : 1, y: recording ? -10 : 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="pt-14 px-6 pb-4 flex items-center justify-between relative z-20"
      >
        <div className="flex flex-col">
          <span className="text-[9px] uppercase tracking-[0.35em] text-white/40">
            Recording Booth
          </span>
          <span className="text-[11px] font-serif italic text-white/60 mt-0.5">
            {sceneLabel}
          </span>
        </div>
        <button
          onClick={() => navigate('/room')}
          className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-white/70 active:bg-white/5 transition-colors"
          aria-label="Leave Recording Booth"
        >
          <XIcon className="w-4 h-4" />
        </button>
      </motion.div>

      {/* Calm reading surface. Per Ryan iPhone test 2026-06-25: the
          auto-advancing on-screen teleprompter was misaligning with her
          real reading pace and being more distracting than helpful. She
          reads from her physical script; the screen stays quiet. The
          silence-detect still runs under the hood so line marks get
          logged for "skip my part" playback (best-effort). */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 text-center relative z-10 gap-7">
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/40">
          Reading
        </p>
        <p className="font-serif italic text-2xl text-white/85 leading-snug max-w-[280px]">
          {script.title}
        </p>
        <p className="font-mono text-[9px] uppercase tracking-[0.28em] text-white/30 max-w-[260px] leading-relaxed">
          Read from your script.<br />Take pauses naturally — we're listening.
        </p>

        {/* Breathing dot — only visible when recording, signals the mic is alive */}
        <AnimatePresence>
          {recording && (
            <motion.div
              key="breath"
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.6 }}
              className="mt-4"
            >
              <motion.span
                className="block w-2 h-2 rounded-full bg-rose-400"
                animate={{ opacity: [0.4, 1, 0.4], scale: [1, 1.4, 1] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Recording Controls — visible throughout the take so the actor can
          glance over and tap stop. (Prior version receded to 0.35 opacity
          which made the stop button hard to find when she's reading from
          her paper script and looks back at the phone.) */}
      <div className="px-7 pb-12 relative z-20 flex flex-col items-center">

        <div className="h-6 mb-6 flex items-center justify-center">
          <AnimatePresence mode="wait">
            {error ? (
              <motion.div
                key="err"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-[10px] text-rose-300/80 max-w-[260px] text-center"
              >
                {error}
              </motion.div>
            ) : uploading ? (
              <motion.div
                key="up"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="font-mono text-[10px] tracking-[0.25em] text-white/60"
              >
                SAVING TAKE…
              </motion.div>
            ) : recording ? (
              <motion.div
                key="timer"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="font-mono text-[11px] tracking-[0.2em] text-rose-400/90 flex items-center gap-2"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                {formatTime(time)}
              </motion.div>
            ) : (
              <motion.p
                key="hint"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-[9px] uppercase tracking-[0.3em] text-white/40"
              >
                Tap to record scene
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        <button
          onClick={toggleRecording}
          disabled={uploading}
          aria-label={recording ? 'Stop recording' : 'Start recording'}
          className={`w-16 h-16 rounded-full flex items-center justify-center shadow-2xl transition-all duration-500 ${
            recording
              ? 'bg-rose-900/30 border border-rose-500/30 scale-90 hover:bg-rose-900/50'
              : 'bg-rose-600 hover:bg-rose-500 hover:scale-105'
          }`}
        >
          {recording ? (
            <SquareIcon className="w-5 h-5 text-rose-400 fill-rose-400/50" />
          ) : (
            <MicIcon className="w-6 h-6 text-white" />
          )}
        </button>
      </div>
    </div>
  );
}
